import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Plus, FileText, Download, Trash2, Eye, ExternalLink, Calendar, Users, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import QuoteEditor from "@/components/quotes/QuoteEditor";

const STATUS_LABEL = {
  draft: { label: "Nháp", cls: "bg-slate-500/20 text-slate-300" },
  sent: { label: "Đã gửi", cls: "bg-sky-500/20 text-sky-300" },
  approved: { label: "Đã duyệt", cls: "bg-emerald-500/20 text-emerald-300" },
  cancelled: { label: "Hủy", cls: "bg-red-500/20 text-red-300" },
};

const formatVND = (n) => `${(n || 0).toLocaleString("vi-VN")}đ`;

export default function QuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data.quotes || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi tải danh sách báo giá");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleNew = () => { setEditing(null); setEditorOpen(true); };
  const handleEdit = (q) => { setEditing(q); setEditorOpen(true); };

  const handleDelete = async (q) => {
    if (!window.confirm(`Xóa báo giá ${q.code}?\nKhách hàng: ${q.customer_name}\nThao tác này không thể hoàn tác.`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success(`Đã xóa ${q.code}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi xóa");
    }
  };

  const handleDownloadPdf = async (q) => {
    try {
      const resp = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", `quote-${q.code}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Lỗi tải PDF");
    }
  };

  const handleOpenPublic = (q) => {
    const publicUrl = `${window.location.origin}/api/public/quotes/${q.code}`;
    window.open(publicUrl, "_blank");
  };

  const handleCopyShareLink = async (q) => {
    const publicUrl = `${window.location.origin}/api/public/quotes/${q.code}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(`Đã sao chép link chia sẻ: ${publicUrl}`);
    } catch {
      window.prompt("Sao chép link chia sẻ:", publicUrl);
    }
  };

  const handleStatusChange = async (q, newStatus) => {
    try {
      await api.patch(`/quotes/${q.id}/status`, { status: newStatus });
      toast.success(`Trạng thái: ${STATUS_LABEL[newStatus].label}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi đổi trạng thái");
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] p-4 sm:p-6 lg:p-8" data-testid="quotes-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-50 flex items-center gap-3">
              <FileText className="w-8 h-8 text-lime-400" />
              Báo giá thuê sân
            </h1>
            <p className="text-slate-400 text-sm mt-1">Quản lý & xuất PDF báo giá thuê sân tổ chức giải</p>
          </div>
          <Button onClick={handleNew} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-2"
            data-testid="quote-new-btn">
            <Plus className="w-4 h-4" /> Tạo báo giá mới
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-lime-400 animate-spin" /></div>
        ) : quotes.length === 0 ? (
          <Card className="bg-[#1e293b] border-[#334155] p-12 text-center" data-testid="quote-empty-state">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-300 font-semibold mb-2">Chưa có báo giá nào</p>
            <p className="text-slate-500 text-sm mb-6">Tạo báo giá đầu tiên để bắt đầu</p>
            <Button onClick={handleNew} className="bg-lime-500 text-[#0f172a]"><Plus className="w-4 h-4 mr-1" /> Tạo báo giá</Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="quote-list">
            {quotes.map((q) => {
              const c = q.calc || {};
              const status = STATUS_LABEL[q.status || "draft"];
              return (
                <Card key={q.id} className="bg-[#1e293b] border-[#334155] p-4 hover:border-lime-500/40 transition-colors"
                  data-testid={`quote-card-${q.code}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="font-mono font-bold text-lime-400">{q.code}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded ${status.cls}`}>{status.label}</span>
                        <span className="text-xs text-slate-500">{q.created_at?.slice(0, 10)}</span>
                      </div>
                      <div className="text-slate-100 font-semibold">{q.customer_name}</div>
                      {q.event_name && <div className="text-sm text-slate-400 italic">{q.event_name}</div>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{c.sessions?.length || 0} phiên</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{c.total_court_hours?.toFixed(1) || 0} sân-giờ</span>
                        <span className="text-slate-500">{q.customer_phone}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Tổng thanh toán</div>
                      <div className="text-2xl font-bold text-lime-400 font-mono">{formatVND(c.total_net)}</div>
                      {c.discount_pct > 0 && (
                        <div className="text-[10px] text-amber-400">−{c.discount_pct}% ({formatVND(c.discount_amount)})</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Button onClick={() => handleEdit(q)} size="sm" variant="outline"
                      className="border-[#334155] text-slate-300 hover:bg-[#0f172a]" data-testid={`quote-edit-${q.code}`}>
                      <Eye className="w-3 h-3 mr-1" /> Xem / Sửa
                    </Button>
                    <Button onClick={() => handleDownloadPdf(q)} size="sm" variant="outline"
                      className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`quote-pdf-${q.code}`}>
                      <Download className="w-3 h-3 mr-1" /> PDF
                    </Button>
                    <Button onClick={() => handleOpenPublic(q)} size="sm" variant="outline"
                      className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10" data-testid={`quote-public-${q.code}`}>
                      <ExternalLink className="w-3 h-3 mr-1" /> Xem online
                    </Button>
                    <Button onClick={() => handleCopyShareLink(q)} size="sm" variant="outline"
                      className="border-[#334155] text-slate-300 hover:bg-[#0f172a]">
                      Sao chép link
                    </Button>
                    <select
                      value={q.status || "draft"}
                      onChange={(e) => handleStatusChange(q, e.target.value)}
                      className="text-xs bg-[#0f172a] border border-[#334155] text-slate-300 rounded px-2 py-1"
                      data-testid={`quote-status-${q.code}`}
                    >
                      <option value="draft">Nháp</option>
                      <option value="sent">Đã gửi</option>
                      <option value="approved">Đã duyệt</option>
                      <option value="cancelled">Hủy</option>
                    </select>
                    <div className="ml-auto">
                      <Button onClick={() => handleDelete(q)} size="sm" variant="outline"
                        className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`quote-delete-${q.code}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <QuoteEditor open={editorOpen} onOpenChange={setEditorOpen} initial={editing} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}
